import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface SingleSelectItemState {
  selected?: number;
  disabled: number[];
}

export type SingleSelectSliceState = Record<string, SingleSelectItemState>;

const getDefaultState = (): SingleSelectItemState => ({
  disabled: [],
});

const singleSelectSlice = createSlice({
  name: "cscq",
  initialState: {} as SingleSelectSliceState,
  reducers: {
    selectOption: (
      state,
      action: PayloadAction<{ componentID: string; optionID: number }>
    ) => {
      const { componentID, optionID } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      state[componentID].selected = optionID;
    },
    submit: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      const current = state[componentID] || getDefaultState();
      const disabledList = [...current.disabled];
      if (current.selected !== undefined) {
        disabledList.push(current.selected);
      }
      // Preserve `selected` across submit so the radio group keeps exposing the
      // submitted option as aria-checked and re-clicking the same option does
      // not desync (Radix skips onValueChange for the already-checked value).
      state[componentID] = {
        selected: current.selected,
        disabled: disabledList,
      };
    },
    reset: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      state[componentID] = { disabled: [] };
    },
  },
  extraReducers: (builder) => {
    const isLegacy = (action: any, type: string) =>
      action.type === type && !action._fromTranslator;

    builder
      .addMatcher(
        (action) => isLegacy(action, "CSCQ/Select Option"),
        (state, action: any) => {
          const { componentID, optionID } = action.payload;
          if (!state[componentID]) {
            state[componentID] = getDefaultState();
          }
          state[componentID].selected = optionID;
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "CSCQ/Submit"),
        (state, action: any) => {
          const componentID = action.payload;
          const current = state[componentID] || getDefaultState();
          const disabledList = [...current.disabled];
          if (current.selected !== undefined) {
            disabledList.push(current.selected);
          }
          // Mirror the modern `submit` reducer: preserve `selected` on submit.
          state[componentID] = {
            selected: current.selected,
            disabled: disabledList,
          };
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "CSCQ/Reset"),
        (state, action: any) => {
          const componentID = action.payload;
          state[componentID] = { disabled: [] };
        }
      );
  },
});

export const singleSelectActions = singleSelectSlice.actions;
export const singleSelectReducer = singleSelectSlice.reducer;

export const selectSingleSelectState = (
  state: { cscq: SingleSelectSliceState },
  componentId: string
) => state.cscq?.[componentId] || getDefaultState();

/** @deprecated Use singleSelectActions */
export const cscqActions = singleSelectActions;
/** @deprecated Use singleSelectReducer */
export const cscqReducer = singleSelectReducer;
/** @deprecated Use selectSingleSelectState */
export const selectCSCQState = selectSingleSelectState;
/** @deprecated Use SingleSelectSliceState */
export type CSCQState = SingleSelectSliceState;
